import React, { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, PackageX, ImageOff, ShoppingCart, Check } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppButton';
import { useCart } from '../context/CartContext';
import api from '../api';

const BRANDS = ['Hepsi', 'DYO', 'BASF', 'Sintaş'];
const CATEGORIES = ['Hepsi', 'Oto Boya', 'Dış Cephe', 'İç Cephe', 'Astar', 'Tiner'];
const STOCK_OPTIONS = ['Hepsi', 'Stokta Var', 'Stokta Yok'];
const WHATSAPP_NUMBER = '905412665154';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('Hepsi');
  const [categoryFilter, setCategoryFilter] = useState('Hepsi');
  const [stockFilter, setStockFilter] = useState('Hepsi');

  useEffect(() => {
    let active = true;

    const fetchProducts = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (brandFilter !== 'Hepsi') params.brand = brandFilter;
        if (categoryFilter !== 'Hepsi') params.category = categoryFilter;
        if (stockFilter === 'Stokta Var') params.stock = 'true';

        const { data } = await api.get('/api/products', { params });
        if (!active) return;
        setProducts(data || []);
      } catch (err) {
        if (!active) return;
        setError('Ürünler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setProducts([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchProducts();
    return () => { active = false; };
  }, [brandFilter, categoryFilter, stockFilter]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name?.toLowerCase().includes(search.toLowerCase());
      const matchesStock =
        stockFilter !== 'Stokta Yok' || !product.stock_status;
      return matchesSearch && matchesStock;
    });
  }, [products, search, stockFilter]);

  const askAboutProduct = (product) => {
    const message = `Merhaba, ${product.name} ürünü hakkında bilgi almak istiyorum.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="bg-bg-primary min-h-screen">
      {/* Page Header */}
      <section className="pt-40 pb-16 bg-bg-primary border-b border-border-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 space-y-4">
          <p className="text-accent-gold font-semibold tracking-[0.3em] uppercase text-sm">Ürün Kataloğu</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">Ürünlerimiz</h1>
          <div className="w-16 h-0.5 bg-accent-gold"></div>
          <p className="text-text-muted font-light max-w-2xl leading-relaxed">
            DYO, BASF ve Sintaş gibi premium markaların geniş ürün yelpazesini Öncü Boya güvencesiyle inceleyin.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-bg-secondary border-b border-border-soft sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 space-y-5">
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ürün adına göre ara..."
              className="w-full pl-11 pr-5 py-3 bg-white/5 border border-border-soft rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-accent-gold transition font-medium"
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <FilterGroup label="Marka" options={BRANDS} value={brandFilter} onChange={setBrandFilter} />
            <FilterGroup label="Kategori" options={CATEGORIES} value={categoryFilter} onChange={setCategoryFilter} />
            <FilterGroup label="Stok Durumu" options={STOCK_OPTIONS} value={stockFilter} onChange={setStockFilter} />
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          {loading && (
            <div className="flex flex-col items-center justify-center py-24 text-text-muted">
              <Loader2 className="animate-spin text-accent-gold mb-4" size={40} />
              <p className="font-semibold">Ürünler yükleniyor...</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-24">
              <p className="text-accent-red font-bold text-lg">{error}</p>
            </div>
          )}

          {!loading && !error && filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-text-muted">
              <PackageX size={56} className="mb-4 opacity-30" />
              <p className="font-bold text-lg text-white">Ürün bulunamadı.</p>
              <p className="text-sm mt-2">Filtreleri değiştirerek tekrar deneyebilirsiniz.</p>
            </div>
          )}

          {!loading && !error && filteredProducts.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} onAskAbout={askAboutProduct} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function FilterGroup({ label, options, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-bold text-text-muted uppercase tracking-widest mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-white/5 border border-border-soft rounded-xl text-white focus:outline-none focus:border-accent-gold transition font-medium"
      >
        {options.map(opt => <option key={opt} value={opt} className="bg-bg-secondary">{opt}</option>)}
      </select>
    </div>
  );
}

function ProductCard({ product, onAskAbout }) {
  const inStock = !!product.stock_status;
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = () => {
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="group bg-[#141414] border border-white/10 rounded-2xl overflow-hidden hover:border-accent-gold/50 transition-all duration-500 hover:-translate-y-2 flex flex-col">
      {/* Image */}
      <div className="relative h-56 overflow-hidden bg-[#0a0a0a] flex-shrink-0">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <ImageOff size={48} />
          </div>
        )}
        {product.brand && (
          <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold bg-black/60 backdrop-blur-sm text-white border border-white/20">
            {product.brand}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1 space-y-3">
        {product.category && (
          <p className="text-accent-gold text-xs font-bold uppercase tracking-wider">{product.category}</p>
        )}

        <h3 className="text-xl font-bold text-white">{product.name}</h3>

        {product.description && (
          <p className="text-white/50 text-sm line-clamp-2 flex-1">{product.description}</p>
        )}

        <div className="flex items-center justify-between pt-2">
          {product.price != null && (
            <span className="text-2xl font-black text-white">
              ₺{Number(product.price).toLocaleString('tr-TR')}
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
            inStock ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {inStock ? 'Stokta Var' : 'Tükendi'}
          </span>
        </div>

        <button
          onClick={handleAddToCart}
          disabled={!inStock}
          className={`w-full mt-2 font-bold py-3 rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 ${
            added
              ? 'bg-green-600 text-white'
              : inStock
              ? 'bg-accent-red hover:bg-red-700 text-white'
              : 'bg-white/10 text-white/30 cursor-not-allowed'
          }`}
        >
          {added ? <><Check size={18} /> Eklendi!</> : <><ShoppingCart size={18} /> Sepete Ekle</>}
        </button>

        <button
          onClick={() => onAskAbout(product)}
          className="w-full bg-white/5 border border-white/10 hover:border-[#25D366]/50 hover:bg-[#25D366]/10 text-white/70 hover:text-white font-semibold py-2.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm"
        >
          <WhatsAppIcon size={15} />
          Bilgi Al
        </button>
      </div>
    </div>
  );
}
